import { useAuth } from '../contexts/AuthContext';
import { getCurrencyConfig, formatCurrency, formatCurrencyCompact, getCurrencySymbol, DEFAULT_CURRENCY } from '../utils/currency';
import type { CurrencyConfig } from '../utils/currency';

/**
 * Hook to get tenant-specific currency information
 * Returns the currency config and formatting functions based on the user's tenant
 */
export const useCurrency = () => {
    const { user } = useAuth();
    
    // Get currency code from tenant (default to RWF)
    const currencyCode = user?.tenant?.default_currency || DEFAULT_CURRENCY;
    const config = getCurrencyConfig(currencyCode);
    
    return {
        /** Current tenant's currency code (e.g., 'RWF', 'USD') */
        currencyCode,
        /** Current tenant's currency symbol (e.g., 'FR', '$') */
        currencySymbol: config.symbol,
        /** Full currency configuration */
        currencyConfig: config,
        /** Format amount with currency symbol */
        format: (amount: number | string | null | undefined, options?: Parameters<typeof formatCurrency>[2]) => 
            formatCurrency(amount, currencyCode, options),
        /** Format compact amount (e.g., "1.5M", "500K") */
        formatCompact: (amount: number | string | null | undefined) => 
            formatCurrencyCompact(amount, currencyCode),
        /** Get currency symbol only */
        symbol: getCurrencySymbol(currencyCode),
    };
};

export default useCurrency;
